import * as React from 'react'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { Destination, IGraphArrayResponse } from '../../context/AxiosContext'
import { driveItemOverviewProperties } from '../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Axios } from 'axios'
import {
    FlatTree,
    FlatTreeItemProps,
    FlatTreeProps,
    HeadlessFlatTreeItem,
    HeadlessFlatTreeItemProps,
    TreeItemValue,
    TreeSelectionValue,
    useHeadlessFlatTree_unstable,
} from '@fluentui/react-components'
import { HeadlessFlatTree, TreeCheckedChangeData } from '@fluentui/react-tree'
import SimpleDocumentFlatTreeItem from './SimpleDocumentFlatTreeItem'
import { DocumentSortOrder } from '../../config/documentConfig'
import { DateTime } from 'luxon'

export enum DocumentSelectionStrategy {
    Single = 'SINGLE',
    HierarchyBackTrack = 'HIERARCHY_BACK_TRACK',
}

export type DocumentFlatTreeProps = FlatTreeProps & {
    rootId: string
    sortOrder: DocumentSortOrder
    selectionStrategy: DocumentSelectionStrategy
    selectedOptions: string[]
    onOptionsSelect: (ids: string[], items: FolderDriveItem[]) => void
    setIsLoading?: (isLoading: boolean) => void
    onDriveItemsLoaded?: (driveItems: FolderDriveItem[]) => void
    setUpdateDriveItems?: (
        setter: (addDriveItems: DriveItem[], removeDriveItems: DriveItem[], filter?: string, sort?: 'asc' | 'desc') => void,
    ) => void
    setReloadDriveItems?: (setter: () => void) => void
    treeItemContent?: (flatTree: any, props: DocumentFlatTreeProps, itemProps: HeadlessFlatTreeItem<FolderDriveItem>) => React.ReactNode
}

export type DocumentFlatTreeItemProps = FlatTreeItemProps &
    DocumentFlatTreeProps & {
        item: FolderDriveItem
        flatTreeHook: HeadlessFlatTree<FolderDriveItem>
        // size: number | 'small' | 'medium'
    }

export type FolderDriveItem = HeadlessFlatTreeItemProps &
    DriveItem & {
        parent: FolderDriveItem
        children: FolderDriveItem[]
        childCount: number
    }

const mapDriveItem = (item: DriveItem, parentItem: FolderDriveItem, rootId: string): FolderDriveItem => {
    return {
        ...item,
        value: item.id,
        parentValue: item.parentReference.id === rootId ? undefined : item.parentReference.id,
        itemType: item.folder ? 'branch' : 'leaf',
        parent: parentItem,
        children: [],
        childCount: item.folder?.childCount || 0,
    }
}

const fetchFolderDriveItemChildren = (
    axios: Axios,
    id: string,
    nextLink?: string,
    foldersOnly?: boolean,
): Promise<IGraphArrayResponse<DriveItem>> => {
    return nextLink
        ? (axios.get(nextLink, { baseURL: Destination.Documents, headers: { SuppressErrors: 'true' } }) as Promise<IGraphArrayResponse<DriveItem>>)
        : (
              axios.get(`/items/${id}/children`, {
                  baseURL: Destination.Documents,
                  params: { $select: driveItemOverviewProperties.join(','), $filter: foldersOnly ? 'folder ne null' : undefined },
                  headers: { SuppressErrors: 'true' },
              }) as Promise<IGraphArrayResponse<DriveItem>>
          ).then((response: IGraphArrayResponse<DriveItem>) => {
              if (response.data['@odata.nextLink']) {
                  return fetchFolderDriveItemChildren(axios, id, response.data['@odata.nextLink'], foldersOnly).then(response2 => ({
                      ...response,
                      ...response2,
                      data: { ...response.data, ...response2.data, value: [...response.data?.value, ...response2.data?.value] },
                  }))
              } else {
                  return response
              }
          })
}

const mapResponse = (
    axios: Axios,
    driveItem: DriveItem,
    parentItem: FolderDriveItem,
    rootId: string,
    foldersOnly: boolean,
): Promise<FolderDriveItem[]> => {
    return Promise.resolve(mapDriveItem(driveItem, parentItem, rootId)).then(folderDriveItem => {
        if (folderDriveItem.childCount > 0) {
            return fetchFolderDriveItemChildren(axios, folderDriveItem.id, undefined, foldersOnly)
                .then(children => {
                    return children.data?.value?.flatMap(child => mapResponse(axios, child, folderDriveItem, rootId, foldersOnly)) || []
                })
                .then(result => Promise.all(result))
                .then(children => (children.length === 0 ? [] : children.reduce((a, b) => a.concat(b))))
                .then(children => {
                    if (parentItem === undefined) {
                        return [...children]
                    } else {
                        return [
                            { ...folderDriveItem, children: children, childCount: children.filter(child => child.itemType === 'leaf').length },
                            ...children,
                        ]
                    }
                })
        } else {
            if (parentItem === undefined) {
                return Promise.resolve([])
            } else {
                return Promise.resolve([folderDriveItem])
            }
        }
    })
}

export const fetchFolderDriveItemTree = (axios: Axios, id: string, foldersOnly: boolean = false): Promise<FolderDriveItem[]> => {
    return axios
        .get<DriveItem>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(','), $expand: 'children' },
            headers: { SuppressErrors: 'true' },
        })
        .then(response => {
            return mapResponse(axios, response.data, undefined, response.data.id, foldersOnly)
        })
}

const sortDriveItems = (sortOrder: DocumentSortOrder): ((a: FolderDriveItem, b: FolderDriveItem) => number) => {
    return (a: FolderDriveItem, b: FolderDriveItem) => {
        if (a.itemType === 'branch' && b.itemType !== 'branch') {
            return -1
        } else if (b.itemType === 'branch' && a.itemType !== 'branch') {
            return 1
        }

        if (a.id === b.parent?.id) {
            return -1
        } else if (b.id === a.parent?.id) {
            return 1
        }

        switch (sortOrder) {
            case DocumentSortOrder.NameAsc:
                return a.name.localeCompare(b.name)
            case DocumentSortOrder.NameDesc:
                return b.name.localeCompare(a.name)
            case DocumentSortOrder.ModifiedAsc:
                return DateTime.fromISO(a.lastModifiedDateTime).toMillis() < DateTime.fromISO(b.lastModifiedDateTime).toMillis() ? -1 : 1
            case DocumentSortOrder.ModifiedDesc:
                return DateTime.fromISO(b.lastModifiedDateTime).toMillis() < DateTime.fromISO(a.lastModifiedDateTime).toMillis() ? -1 : 1
        }

        return 0
    }
}

export default function DocumentFlatTree(props: DocumentFlatTreeProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const [items, setItems] = useState<FolderDriveItem[]>(undefined)
    const [checkedItems, setCheckedItems] = useState<Map<TreeItemValue, TreeSelectionValue>>(new Map())

    const updateDriveItemsFunc = useCallback(
        (addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) => {
            setItems(prevItems => {
                const removeDriveItemIds = removeDriveItems?.map(driveItem => driveItem.id) || []

                const existingDriveItems = [...prevItems.filter(prevItem => !removeDriveItemIds.includes(prevItem.id))]

                const result = [
                    ...existingDriveItems,
                    ...(addDriveItems?.map(driveItem =>
                        mapDriveItem(
                            driveItem,
                            prevItems?.find(folderDriveItem => driveItem.parentReference.id === folderDriveItem.id),
                            props.rootId,
                        ),
                    ) || []),
                ].sort(sortDriveItems(props.sortOrder))
                props.onDriveItemsLoaded?.(result)
                return result
            })
        },
        [props.rootId],
    )

    const reloadDriveItemsFunc = useCallback(() => {
        if (props.rootId) {
            props.setIsLoading?.(true)
            fetchFolderDriveItemTree(axios, props.rootId).then(result => {
                result.sort(sortDriveItems(props.sortOrder))
                setItems(result)
                props.onDriveItemsLoaded?.(result)
                props.setIsLoading?.(false)
            })
        } else {
            setItems(undefined)
        }
    }, [props.rootId])

    useEffect(() => {
        setItems(prevItems => (prevItems ? [...prevItems] : []).sort(sortDriveItems(props.sortOrder)))
    }, [props.sortOrder])

    useEffect(() => {
        props.setUpdateDriveItems?.((addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) =>
            updateDriveItemsFunc(addDriveItems, removeDriveItems),
        )
        props.setReloadDriveItems?.(() => reloadDriveItemsFunc())

        return () => props.setUpdateDriveItems?.(() => {}) // Cleanup
    }, [])

    const flatTree = useHeadlessFlatTree_unstable(items, {
        selectionMode: 'multiselect',
        onCheckedChange: (_, data: TreeCheckedChangeData) => {
            let newCheckedItems: Map<TreeItemValue, TreeSelectionValue> = new Map(checkedItems)
            let newSelectedDriveItemIds: string[] = []
            let newSelectedDriveItems: FolderDriveItem[] = []

            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    if (data.checked) {
                        newCheckedItems.set(data.value, data.checked)
                    } else {
                        newCheckedItems.delete(data.value)
                    }
                    newSelectedDriveItemIds = Array.from(newCheckedItems.keys()) as string[]
                    newSelectedDriveItems = Array.from(newCheckedItems)
                        .filter(([, checkedValue]) => checkedValue === true)
                        .flatMap(([checkedItem]) => items.filter(item => checkedItem === item.id))

                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    newCheckedItems = new Map(data.checkedItems)

                    newSelectedDriveItemIds = Array.from(data.checkedItems)
                        .filter(
                            ([checkedItem, checkedValue]) =>
                                items.find(item => item.value === checkedItem)?.itemType === 'leaf' && checkedValue === true,
                        )
                        .map(([item]) => item as string)
                    newSelectedDriveItems = items.filter(item => newSelectedDriveItemIds.includes(item.id))

                    break
                }
            }

            props.onOptionsSelect?.(newSelectedDriveItemIds, newSelectedDriveItems)
            // setCheckedItems(newCheckedItems)
        },
        checkedItems: checkedItems,
    })

    useEffect(() => {
        reloadDriveItemsFunc()
    }, [props.rootId])

    useEffect(() => {
        if (items) {
            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => items.find(item => item.value === value))
                                .filter(Boolean)
                                .map(value => [value.id, true as TreeSelectionValue]),
                        ),
                    )
                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => items.find(item => item.value === value))
                                .filter(Boolean)
                                .flatMap(value => {
                                    const hierarchy: [TreeItemValue, TreeSelectionValue][] = []
                                    hierarchy.push([value.id, true])
                                    let parentItem = items.find(item => item.value === value.parentValue)
                                    while (parentItem !== undefined) {
                                        hierarchy.push([parentItem.id, 'mixed'])
                                        parentItem = items.find(item => item.value === parentItem.parentValue)
                                    }
                                    return hierarchy
                                }) || [],
                        ),
                    )
                    break
                }
            }
        }
    }, [items, props.selectedOptions])

    if (!items) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <FlatTree {...props} {...flatTree.getTreeProps()} aria-label={items?.[0]?.name}>
            {Array.from(flatTree.items(), flatTreeItem => {
                return (
                    props.treeItemContent?.(flatTree, props, flatTreeItem) || (
                        <SimpleDocumentFlatTreeItem
                            key={flatTreeItem.value}
                            {...flatTreeItem.getTreeItemProps()}
                            {...props}
                            item={flatTreeItem.getTreeItemProps()}
                            flatTreeHook={flatTree}
                            itemType={flatTreeItem.itemType}
                            size={props.size}
                        />
                    )
                )
            })}
        </FlatTree>
    )
}
